/**
 * Shared HTTP rules of every source client (sources.md "Common rules"): conditional requests, status →
 * error mapping, Retry-After, URL canonicalisation. Pure helpers; the clients do their I/O through
 * `SourceRuntime.fetcher` only.
 */

import type { ErrorDetails } from '../contracts/errors.js';
import type { EpochMs } from '../contracts/ids.js';
import type { FetchResponse } from '../contracts/platform.js';
import type { SourceFetchContext } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';


/** `If-None-Match` / `If-Modified-Since` from the previous successful fetch. */
export function conditionalHeaders(etag: string | null | undefined, lastModified: string | null | undefined): Record<string, string> {
  const headers: Record<string, string> = {};
  if (etag) headers['If-None-Match'] = etag;
  if (lastModified) headers['If-Modified-Since'] = lastModified;
  return headers;
}

export function contextConditionalHeaders(ctx: Pick<SourceFetchContext, 'etag' | 'lastModified'>): Record<string, string> {
  return conditionalHeaders(ctx.etag, ctx.lastModified);
}

/** The validators of a successful response (null when the origin sent none). */
export function validatorsOf(response: FetchResponse): { etag: string | null; lastModified: string | null } {
  return { etag: response.headers['etag'] ?? null, lastModified: response.headers['last-modified'] ?? null };
}

/** Milliseconds from `now` named by a Retry-After header (delta-seconds or an HTTP date), or null. */
export function parseRetryAfter(value: string | undefined, now: number): number | null {
  if (value === undefined) return null;
  const v = value.trim();
  if (/^\d+$/.test(v)) return Number(v) * 1000;
  const at = Date.parse(v);
  if (Number.isNaN(at)) return null;
  return Math.max(0, at - now);
}

/**
 * rate_limited with the origin's wait when it named one. Without it, `retryAfterMs` is absent and the
 * engine waits engine_config `fetch.default_retry_after_minutes` (engine/pipeline/harvest.ts).
 */
export function rateLimited(message: string, retryAfterMs: number | null, extra: ErrorDetails = {}): DigesterError {
  return new DigesterError('rate_limited', message, retryAfterMs === null ? { ...extra } : { ...extra, retryAfterMs });
}

export interface StatusOptions {
  /** The request carried a credential (token, session): 401/403 then mean the credential was refused. */
  credential: boolean;
  /** Human name of what was fetched, for messages. */
  what: string;
  now: number;
}

/**
 * Throw the sources.md error for a non-success status. 2xx returns; 304 is the caller's business (it
 * returns `notModified`) and is accepted here too.
 */
export function assertOk(response: FetchResponse, options: StatusOptions): void {
  const s = response.status;
  if ((s >= 200 && s < 300) || s === 304) return;
  const details: ErrorDetails = { status: s };
  if ((s === 401 || s === 403) && options.credential) {
    throw new DigesterError('source_auth_failed', `${options.what} rejected the credential (HTTP ${s}).`, details);
  }
  if (s === 429 || s === 503) {
    throw rateLimited(`${options.what} asked us to slow down (HTTP ${s}).`, parseRetryAfter(response.headers['retry-after'], options.now), details);
  }
  if (s === 404 || s === 410) throw new DigesterError('fetch_failed', `${options.what} was not found (HTTP ${s}).`, details);
  throw new DigesterError('fetch_failed', `${options.what} answered HTTP ${s}.`, details);
}

/** JSON.parse that fails with parse_failed. */
export function parseJson(text: string, what: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new DigesterError('parse_failed', `${what} did not return valid JSON.`);
  }
}

export function invalidInput(message: string, path = 'address'): DigesterError {
  return new DigesterError('invalid_input', message, { issues: [{ path, message }] });
}

/** Parse an http(s) URL; a bare host/path gets `https://`. Null when it is not one. */
export function parseHttpUrl(input: string): URL | null {
  const raw = input.trim();
  if (raw === '') return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.hostname === '') return null;
    return url;
  } catch {
    return null;
  }
}

/**
 * sources.md rss "Locator": scheme and host lower-cased, default port and fragment removed. Path and
 * query are kept exactly (they are case-sensitive on most servers).
 */
export function canonicalUrl(input: string): string | null {
  const url = parseHttpUrl(input);
  if (!url) return null;
  url.hash = '';
  // URL already lower-cases scheme and host and drops the scheme's default port.
  return url.toString();
}

/** Resolve `href` against `base`; null unless the result is absolute http(s). */
export function absoluteUrl(href: string | null | undefined, base: string | null | undefined): string | null {
  if (!href) return null;
  try {
    const url = base ? new URL(href.trim(), base) : new URL(href.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

/** Escape text for HTML element content and attribute values. */
export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Epoch ms of a date string, or undefined when absent/unparseable. */
export function parseDate(value: string | null | undefined): EpochMs | undefined {
  if (!value) return undefined;
  const ms = Date.parse(value.trim());
  return Number.isNaN(ms) ? undefined : (ms as EpochMs);
}

/** Keep at most `max` items, newest first (undated last), for clients whose cursor moves to the newest. */
export function newestFirst<T extends { publishedAt?: number }>(items: readonly T[], max: number): T[] {
  const indexed = items.map((item, index) => ({ item, index }));
  indexed.sort((a, b) => {
    const pa = a.item.publishedAt;
    const pb = b.item.publishedAt;
    if (pa !== undefined && pb !== undefined && pa !== pb) return pb - pa;
    if (pa !== undefined && pb === undefined) return -1;
    if (pa === undefined && pb !== undefined) return 1;
    return a.index - b.index;
  });
  return indexed.slice(0, Math.max(0, max)).map((x) => x.item);
}

/** Plain text (with newlines) → HTML paragraphs, http(s) URLs linked. */
export function textToParagraphs(text: string): string {
  const blocks = text.replace(/\r\n?/g, '\n').split(/\n{2,}/).map((b) => b.trim()).filter((b) => b !== '');
  return blocks.map((b) => `<p>${linkify(b).replace(/\n/g, '<br>')}</p>`).join('');
}

function linkify(text: string): string {
  const parts: string[] = [];
  let last = 0;
  for (const m of text.matchAll(/https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]/g)) {
    const index = m.index ?? 0;
    parts.push(escapeHtml(text.slice(last, index)));
    const url = m[0];
    parts.push(`<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`);
    last = index + url.length;
  }
  parts.push(escapeHtml(text.slice(last)));
  return parts.join('');
}
